import React, { useState, useEffect, useRef } from 'react';
import { AppBar, Toolbar, Typography, IconButton } from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import CloseIcon from '@mui/icons-material/Close';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';

const navLinks = [
  { name: 'Inicio', to: '/inicio' },
  { name: 'Nosotros', to: '/nosotros' },
  { name: 'Servicios', to: '/servicios' },
  { name: 'Talleres y Eventos', to: '/talleres' },
  { name: 'Contacto', to: '/contacto' },
];

const Header = ({ isLoggedIn, onLogout }) => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const firstLinkRef = useRef(null);
  const menuButtonRef = useRef(null);

  const toggleMobileMenu = () => {
    setMobileMenuOpen((prev) => !prev);
  };

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === 'Escape' && mobileMenuOpen) {
        setMobileMenuOpen(false);
        menuButtonRef.current.focus();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    if (mobileMenuOpen && firstLinkRef.current) {
      firstLinkRef.current.focus();
    }
  }, [mobileMenuOpen]);

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        className="bg-white shadow-md border-b border-gray-200 h-16"
        style={{ backgroundColor: '#ffffff' }}
      >
        <Toolbar className="flex justify-between items-center h-full">
          {/* Logo con texto */}
          <div className="flex items-center">
            <div className="w-8 h-8 rounded-full mr-2 flex items-center justify-center">
              <img src="images/favicon.ico" alt="Logo" className="w-8 h-8 object-contain" />
            </div>
            <Typography variant="h6" className="text-black font-semibold">
              Ser Consciente
            </Typography>
          </div>

          {/* Navegación para pantallas grandes */}
          <nav className="hidden md:flex">
            <ul className="flex space-x-6">
              {navLinks.map((link) => {
                const isActive = location.pathname === link.to;
                return (
                  <motion.li key={link.name} whileHover={{ scale: 1.1 }} className="list-none">
                    <Link
                      to={link.to}
                      className={`relative font-medium transition-colors duration-300 ${
                        isActive ? 'text-violet-500' : 'text-black hover:text-violet-900'
                      }`}
                    >
                      {link.name}
                      {/* Subrayado animado */}
                      <motion.span
                        className="absolute left-0 -bottom-1 h-0.5 bg-violet-500"
                        initial={{ width: 0 }}
                        animate={{ width: isActive ? '100%' : 0 }}
                        whileHover={{ width: '100%' }}
                        transition={{ duration: 0.3 }}
                      />
                    </Link>
                  </motion.li>
                );
              })}

              {/* Agregar el botón "Cerrar Sesión" dentro de la lista de navegación */}
              {isLoggedIn && (
                <motion.li whileHover={{ scale: 1.1 }} className="list-none">
                  <button
                    onClick={onLogout}
                    className="relative font-medium text-red-500 hover:text-red-700 transition-colors duration-300"
                  >
                    Cerrar Sesión
                  </button>
                </motion.li>
              )}
            </ul>
          </nav>

          {/* Botón para abrir el menú en pantallas pequeñas */}
          <button
            onClick={toggleMobileMenu}
            className="md:hidden flex items-center text-black font-semibold focus:outline-none"
            aria-label="Abrir menú"
            ref={menuButtonRef}
          >
            <MenuOpenIcon className="h-6 w-6 mr-2" />
            <span>Menú</span>
          </button>
        </Toolbar>
      </AppBar>

      {/* Menú alternativo para dispositivos pequeños */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            {/* Fondo semitransparente */}
            <motion.div
              className="fixed inset-0 bg-black opacity-25 z-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.25 }}
              exit={{ opacity: 0 }}
              onClick={toggleMobileMenu}
            />

            {/* Menú alternativo */}
            <motion.div
              className="fixed top-16 left-0 right-0 bg-white shadow-md z-50"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              {/* Botón para cerrar el menú */}
              <div className="flex justify-end p-4">
                <IconButton edge="end" aria-label="Cerrar menú" onClick={toggleMobileMenu} className="text-black">
                  <CloseIcon />
                </IconButton>
              </div>

              {/* Enlaces de navegación */}
              <ul className="flex flex-col space-y-4 px-4 pb-4">
                {navLinks.map((link, index) => {
                  const isActive = location.pathname === link.to;
                  return (
                    <motion.li
                      key={link.name}
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.2, delay: index * 0.05 }}
                      className="list-none"
                    >
                      <Link
                        to={link.to}
                        onClick={toggleMobileMenu}
                        className={`font-medium text-lg ${
                          isActive ? 'text-violet-500' : 'text-black hover:text-violet-900'
                        }`}
                        ref={index === 0 ? firstLinkRef : null}
                      >
                        {link.name}
                      </Link>
                    </motion.li>
                  );
                })}

                {/* Agregar "Cerrar Sesión" en el menú móvil */}
                {isLoggedIn && (
                  <motion.li whileHover={{ scale: 1.1 }} className="list-none">
                    <button
                      onClick={() => {
                        onLogout();
                        toggleMobileMenu();
                      }}
                      className="font-medium text-lg text-red-500 hover:text-red-700 transition-colors duration-300"
                    >
                      Cerrar Sesión
                    </button>
                  </motion.li>
                )}
              </ul>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
